/**
 * A valance as an upper bay's choice (claude-docs/2026-10-30-venus-full-line-design.md,
 * group 3c follow-up) — the one rule the panel, the fill, the width write and
 * the valances read.
 *
 * The user (2026-11-01): "typically someone would want to place this above
 * the sink and have it stay there". An upper bay set to `valance` holds no
 * cabinet: the fill builds the valance board's own box there
 * (`category.valanceBay`) — as thin as the board, its face on the uppers'
 * front line, its top on theirs — so it can be clicked, keeps a width the
 * user sets and answers to its record like any bay. What is drawn and billed
 * is still `cabinet-trim/valances.js`'s valance, hung in the gap the bay
 * leaves between the two uppers either side.
 *
 *  - Tied to the sink base under it: the record names the sink's bay
 *    (`overSinkBayId`, which the fill stamps on every upper it mirrors over a
 *    sink base) and is seated over that base every solve, so moving the sink
 *    moves it.
 *  - Its width: the sink base's when none is set; else the one set, centred
 *    over the sink — only widths a valance of its style comes in
 *    (`valanceWidthsIn`).
 *  - Its style: its own (`valanceStyle`), else the run's Valance row, else
 *    arched.
 *
 * Pure; metres unless named.
 */
const { isUpperCabinet } = require('../kitchen-assembly/overFridgeCap');
const { findRunByIdOrAlias } = require('./runIds');
const { MIN_MODULE_IN } = require('./moduleSolver');
const { baysForRun } = require('./bays');

const IN = 0.0254;
/** Two edges this close touch (the trim's touch tolerance). */
const TOUCH_M = 0.01;
/** The widths a valance bay's chips offer: 3" steps over this range. */
const WIDTH_STEP_IN = 3;
const SMALLEST_IN = 12;

const VALANCE_STYLES = Object.freeze(['arched', 'raised-panel']);
/** Why a valance bay with no upper beside it hangs none. */
const AT_THE_END =
  'A valance hangs between two upper cabinets: this one is at the end';

/** Is this the valance bay's box? */
const isValanceBay = (o) => o?.category?.valanceBay === true;

/**
 * Does a valance hang beside this upper — a box of the row, not boards on
 * the wall, a cabinet standing on the counter or another valance?
 */
const hangsBeside = (o) =>
  isUpperCabinet(o) &&
  !o.category?.stack &&
  !o.category?.floatingShelves &&
  !o.category?.endShelf &&
  !o.category?.wallDrawers &&
  !isValanceBay(o);

/** A bay record's valance style: its own, else the run's, else arched. */
function valanceStyleFor(run, record) {
  const own = record?.valanceStyle;
  if (VALANCE_STYLES.includes(own)) return own;
  const runs = run?.valance?.style;
  return VALANCE_STYLES.includes(runs) ? runs : 'arched';
}

// Required here, not at the top: `cabinet-trim/profiles` reads the families,
// which this module's own readers (`catalog.js`) are required by.
const valanceProfiles = (family, style) =>
  require('../cabinet-trim/profiles')
    .trimProfiles(family, 'valance')
    .filter((p) => p.style === style);

/**
 * The smallest of the line's valances of `style` that covers `lengthM`,
 * trimmed down no shorter than it may be (`trimToM`); null for none.
 */
function valanceSizeFor(family, style, lengthM) {
  return (
    valanceProfiles(family, style)
      .sort((a, b) => a.widthM - b.widthM)
      .find((p) => p.widthM >= lengthM - 1e-6 && p.trimToM <= lengthM + 1e-6) ||
    null
  );
}

/**
 * The widths (inches) a valance bay may be: 3" steps a valance of `style`
 * on this line covers, from 12" to its longest.
 */
function valanceWidthsIn(family, style) {
  const longest = Math.max(
    0,
    ...valanceProfiles(family, style).map((p) => p.widthM / IN)
  );
  const out = [];
  for (let w = Math.max(SMALLEST_IN, MIN_MODULE_IN); w <= longest + 1e-6; ) {
    if (valanceSizeFor(family, style, w * IN)) out.push(w);
    w += WIDTH_STEP_IN;
  }
  return out;
}

/** The board a valance of `style` is on this line — `{ heightM, depthM }`. */
function valanceBoardOf(family, style) {
  const p = valanceProfiles(family, style)[0];
  return p ? { heightM: p.heightM, depthM: p.depthM } : null;
}

/** Where a box sits along its own facing: its low and high edges. */
function spanAlong(o, along) {
  const u = o.center.x * along.x + o.center.z * along.z;
  return { lo: u - o.dimensions.x / 2, hi: u + o.dimensions.x / 2 };
}

/**
 * Why this upper cannot be a valance bay, or null when it can: a valance
 * hangs between two uppers of its run, so it needs one touching each side.
 * Null without the room's objects to read.
 */
function valanceBayReason(run, cabinet, objects) {
  if (!cabinet?.center || !(cabinet.dimensions?.x > 0) || !objects?.length)
    return null;
  const ry = cabinet.rotation?.y || 0;
  const along = { x: Math.cos(ry), z: -Math.sin(ry) };
  const me = spanAlong(cabinet, along);
  const beside = objects.filter(
    (o) =>
      o !== cabinet &&
      o.id !== cabinet.id &&
      hangsBeside(o) &&
      o.center &&
      o.dimensions?.x > 0 &&
      Math.abs(Math.sin((o.rotation?.y || 0) - ry)) < 1e-3 &&
      findRunByIdOrAlias([run], o.runId)
  );
  const touches = (edge, side) =>
    beside.some((o) => Math.abs(spanAlong(o, along)[side] - edge) <= TOUCH_M);
  if (touches(me.lo, 'hi') && touches(me.hi, 'lo')) return null;
  return AT_THE_END;
}

/**
 * The sink bay a valance record standing at `anchorParam` on segment
 * `segIndex` is over, read off the run's own bays — for a record written
 * from an upper that predates the fill's `overSinkBayId` stamp. Null when it
 * is over none.
 */
function sinkBayUnder(run, segIndex, anchorParam) {
  const bay = baysForRun(run).find(
    (b) =>
      b.kind === 'sink-base' &&
      (b.segmentIndex ?? 0) === segIndex &&
      b.width > 0 &&
      Math.abs(anchorParam - b.anchorParam) <= b.width / 2 + 1e-6
  );
  return bay?.id ?? null;
}

module.exports = {
  VALANCE_STYLES,
  AT_THE_END,
  isValanceBay,
  hangsBeside,
  valanceStyleFor,
  valanceSizeFor,
  valanceWidthsIn,
  valanceBoardOf,
  valanceBayReason,
  sinkBayUnder,
};
